import { useState } from 'react';
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const steps = [
  {
    number: "01",
    title: "Crie o Texto",
    description: "Use o ChatGPT para gerar textos criativos para suas legendas e conteúdos."
  },
  {
    number: "02",
    title: "Gere Imagens",
    description: "Transforme suas ideias em imagens impressionantes com Deep AI ou outras ferramentas."
  },
  {
    number: "03",
    title: "Edite Vídeos",
    description: "Combine suas imagens e adicione efeitos com Clideo ou CapCut."
  },
  {
    number: "04",
    title: "Adicione Música",
    description: "Crie músicas originais com Suno AI para complementar seus vídeos."
  },
  {
    number: "05",
    title: "Publique",
    description: "Compartilhe seu conteúdo no Rumble e comece a construir sua audiência."
  }
];

const CreateSection = () => {
  const [open, setOpen] = useState(false);
  
  const handleCreate = () => {
    toast.success("Projeto iniciado! Em breve você poderá criar conteúdo completo.");
    setOpen(false);
  };

  return (
    <section id="create" className="py-20 bg-gradient-to-b from-background to-background/95 px-4 sm:px-6 lg:px-8 relative">
      {/* Background elements */}
      <div className="absolute w-96 h-96 bg-primary/5 rounded-full blur-3xl bottom-20 -right-48"></div>
      
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">
              Como Criar Conteúdo <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Incrível</span>
            </h2>
            
            <div className="space-y-8">
              {steps.map((step, index) => (
                <div key={index} className="flex">
                  <div className="mr-4 flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                      {step.number}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-1">{step.title}</h3>
                    <p className="text-muted-foreground">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="bg-card border border-border rounded-lg p-6 shadow-lg relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-40 h-40 bg-primary/10 rounded-full blur-3xl -mr-20 -mt-20"></div>
            <h3 className="text-2xl font-bold text-card-foreground mb-6 relative z-10">Comece Seu Projeto</h3>
            
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground mb-4">
                  Criar Novo Projeto
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px] bg-card text-card-foreground">
                <DialogHeader>
                  <DialogTitle>Criar Novo Projeto</DialogTitle>
                </DialogHeader>
                <Tabs defaultValue="meme" className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="meme">Meme</TabsTrigger>
                    <TabsTrigger value="video">Vídeo</TabsTrigger>
                    <TabsTrigger value="music">Música</TabsTrigger>
                  </TabsList>
                  <TabsContent value="meme" className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label htmlFor="meme-title">Título do Meme</Label>
                      <Input id="meme-title" placeholder="Digite um título criativo" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="meme-format">Formato</Label>
                      <Select>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o formato" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1:1">1:1 (Quadrado)</SelectItem>
                          <SelectItem value="16:9">16:9 (Paisagem)</SelectItem>
                          <SelectItem value="9:16">9:16 (Retrato)</SelectItem>
                          <SelectItem value="4:5">4:5 (Instagram)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="meme-description">Descrição</Label>
                      <Input id="meme-description" placeholder="Descreva seu meme" />
                    </div>
                    <Button onClick={handleCreate} className="w-full">Criar Meme</Button>
                  </TabsContent>
                  
                  <TabsContent value="video" className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label htmlFor="video-title">Título do Vídeo</Label>
                      <Input id="video-title" placeholder="Digite um título para o vídeo" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="video-format">Formato</Label>
                      <Select>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o formato" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="16:9">16:9 (Paisagem)</SelectItem>
                          <SelectItem value="9:16">9:16 (Retrato/Stories)</SelectItem>
                          <SelectItem value="1:1">1:1 (Quadrado)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="video-duration">Duração (segundos)</Label>
                      <Input id="video-duration" type="number" placeholder="30" />
                    </div>
                    <Button onClick={handleCreate} className="w-full">Criar Vídeo</Button>
                  </TabsContent>
                  
                  <TabsContent value="music" className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label htmlFor="music-title">Título da Música</Label>
                      <Input id="music-title" placeholder="Digite um título para a música" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="music-style">Estilo</Label>
                      <Select>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o estilo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pop">Pop</SelectItem>
                          <SelectItem value="rock">Rock</SelectItem>
                          <SelectItem value="electronic">Eletrônica</SelectItem>
                          <SelectItem value="ambient">Ambiente</SelectItem>
                          <SelectItem value="hiphop">Hip Hop</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="music-duration">Duração (segundos)</Label>
                      <Input id="music-duration" type="number" placeholder="60" />
                    </div>
                    <Button onClick={handleCreate} className="w-full">Criar Música</Button>
                  </TabsContent>
                </Tabs>
              </DialogContent>
            </Dialog>
            
            <div className="space-y-4 relative z-10">
              <div className="flex items-center p-3 bg-secondary/50 rounded-md">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary mr-3">
                  1
                </div>
                <div className="text-muted-foreground">Crie memes com textos personalizados</div>
              </div>
              
              <div className="flex items-center p-3 bg-secondary/50 rounded-md">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary mr-3">
                  2
                </div>
                <div className="text-muted-foreground">Monte vídeos com suas imagens</div>
              </div>
              
              <div className="flex items-center p-3 bg-secondary/50 rounded-md">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary mr-3">
                  3
                </div>
                <div className="text-muted-foreground">Adicione músicas geradas por IA</div>
              </div>
              
              <div className="flex items-center p-3 bg-secondary/50 rounded-md">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary mr-3">
                  4
                </div>
                <div className="text-muted-foreground">Publique diretamente no Rumble</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CreateSection;